import { useInfiniteQuery } from "@tanstack/react-query";
import { useMemo, useCallback } from "react";
import { useDebounce } from "@uidotdev/usehooks";

import { useClientsQuery, ClientDocument, type ClientQuery } from "@graphql/clients/clients.g";
import { InfiniteSelectBase, InfiniteSelectDataHook, SelectOption } from "@components/Selects/InfiniteSelectBase";

const PAGE_SIZE = 30;
const SEARCH_DEBOUNCE = 300;

// Hook to provide clients data for the infinite select
const useInfiniteClients = (
  searchQuery: string,
  isOpen: boolean,
  disabled: boolean
): InfiniteSelectDataHook => {
  const debouncedSearchQuery = useDebounce(searchQuery, SEARCH_DEBOUNCE);

  const query = useInfiniteQuery({
    queryKey: ["InfiniteClients", debouncedSearchQuery],
    queryFn: async ({ pageParam }) => {
      const result = await useClientsQuery.fetcher({
        first: PAGE_SIZE,
        offset: pageParam as number,
        sortBy: "name",
        ...(debouncedSearchQuery && {
          name_Icontains: debouncedSearchQuery,
        }),
      })();
      return result;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const currentOffset = allPages.length * PAGE_SIZE;
      const totalCount = lastPage?.clients?.totalCount || 0;

      if (currentOffset < totalCount) {
        return currentOffset;
      }
      return undefined;
    },
    enabled: isOpen && !disabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const options = useMemo(() => {
    const allOptions: Array<{ id: string; label: string }> = [];

    if (query.data?.pages) {
      query.data.pages.forEach((page) => {
        const clients = page?.clients?.edges || [];
        clients.forEach((edge) => {
          const node = edge?.node;
          if (node) {
            allOptions.push({
              id: node.id,
              label: node.name || "",
            });
          }
        });
      });
    }

    return allOptions;
  }, [query.data]);

  return {
    ...query,
    options,
  };
};

type Props = {
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
};

export const InfiniteClientSelect = ({
  value,
  onChange,
  placeholder = "Search and select client...",
  disabled = false,
}: Props) => {
  const fetchSelectedOption = useCallback(async (id: string): Promise<SelectOption | null> => {
    try {
      const response = await fetch(import.meta.env.VITE_API_URL_GRAPHQL as string, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          query: ClientDocument,
          variables: { clientId: id },
        }),
      });

      const json = await response.json();

      if (json.errors) {
        console.error("GraphQL error fetching client:", json.errors);
        return null;
      }

      const data = json.data as ClientQuery;
      const client = data?.client;

      if (client) {
        return {
          id: client.id,
          label: client.name || "",
        };
      }
    } catch (error) {
      console.error("Failed to fetch selected client:", error);
    }
    return null;
  }, []);

  // Create a wrapper hook that includes disabled in closure
  const useDataWithDisabled = useMemo(() => {
    return (searchQuery: string, isOpen: boolean) => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      return useInfiniteClients(searchQuery, isOpen, disabled);
    };
  }, [disabled]);

  return (
    <InfiniteSelectBase
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      useData={useDataWithDisabled}
      fetchSelectedOption={fetchSelectedOption}
      loadingText="Loading clients..."
      noResultsText="No clients found"
      loadingMoreText="Loading more..."
      disabled={disabled}
    />
  );
};